import { Input as FplInput } from '@figma/fpl-components';
import { color as token, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import { TimecodeInput } from 'weave-timeline';
import { MENU_NAMES } from '@/consts/menu-names.consts';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { AppToggleButton } from '@/UI/AppToggleButtons/AppToggleButton';
import { FplMenuCaretButton } from '@/UI/Buttons/FplMenuCaretButton';
import { CaretIcon } from '@/UI/Icons';
import { FlexCenVer } from '@/UI/styles';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { FrameInput } from './FrameInput';
import { TimeInput } from './TimeInput';
import type { MenuAction } from '@/components/Menu/Actions';
import type { TimeDisplayMode, TimeInputVariant } from '../types';

const DEFAULT_TIME_INPUT_FPS = 30;
const TIME_INPUT_WIDTH = 74;
const FORMAT_CARET_ARIA_LABEL = 'Time format';

function FormatCaretButton({ open, onClick }: { open: boolean; onClick: (event: MouseEvent) => void }) {
  const isFplMigrationEnabled = useFplMigrationFlag();

  // Shared FplMenuCaretButton (painter shape + video time format). AppToggleButton would
  // render as a wide FPL DialogTriggerButton for a popup caret.
  if (isFplMigrationEnabled) {
    return (
      <FplMenuCaretButton aria-expanded={open} aria-label={FORMAT_CARET_ARIA_LABEL} height={24} onClick={onClick} />
    );
  }

  return (
    <AppToggleButton
      value="format-menu"
      selected={open}
      btnW={24}
      btnH={24}
      isIcon
      svgSize={12}
      aria-label={FORMAT_CARET_ARIA_LABEL}
      aria-haspopup="menu"
      sx={{ border: 'none' }}
      onClick={onClick}
    >
      <CaretIcon />
    </AppToggleButton>
  );
}

interface EditableTimeInputProps {
  currentTime: number;
  duration: number;
  fps?: number;
  variant?: TimeInputVariant;
  paused: boolean;
  onTogglePlay: () => void;
  onSetTime: (time: number, ongoing: boolean) => void;
  /** Shares the timecode/frames choice with other inputs (e.g. Trim's Start/End fields). Uncontrolled when omitted. */
  mode?: TimeDisplayMode;
  onModeChange?: (mode: TimeDisplayMode) => void;
}

export function EditableTimeInput({ variant = 'timecode', mode, onModeChange, ...props }: EditableTimeInputProps) {
  if (variant === 'time') {
    return (
      <FlexCenVer sx={{ gap: 0.25, flexShrink: 0, width: 'fit-content' }}>
        <TimeInput {...props} />
      </FlexCenVer>
    );
  }

  return <TimecodeOrFrameInput {...props} mode={mode} onModeChange={onModeChange} />;
}

function ThemedTimecodeInput(props: React.ComponentProps<typeof TimecodeInput>) {
  const isFpl = useFplMigrationFlag();
  return isFpl ? (
    <FplInput.Root>
      <TimecodeInput
        {...props}
        unstyled
        style={{ ...props.style, color: token.text, padding: `0 ${spacer.spacer2}` }}
      />
    </FplInput.Root>
  ) : (
    <TimecodeInput {...props} />
  );
}

function TimecodeOrFrameInput({
  currentTime,
  duration,
  fps,
  paused,
  onTogglePlay,
  onSetTime,
  mode: controlledMode,
  onModeChange,
}: Omit<EditableTimeInputProps, 'variant'>) {
  const [internalMode, setInternalMode] = useState<TimeDisplayMode>('timecode');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const mode = controlledMode ?? internalMode;
  const setMode = useCallback((next: TimeDisplayMode) => (onModeChange ?? setInternalMode)(next), [onModeChange]);

  const showFrames = mode === 'frames' && fps !== undefined;
  const activeMode: TimeDisplayMode = showFrames ? 'frames' : 'timecode';
  const formatActions = useMemo<MenuAction[]>(
    () => [
      {
        biEventKey: { menu: MENU_NAMES.TIME_INPUT, action: 'select_timecode' },
        name: 'Timecode',
        selected: activeMode === 'timecode',
        action: () => {
          setMode('timecode');
          setMenuAnchor(null);
        },
      },
      {
        biEventKey: { menu: MENU_NAMES.TIME_INPUT, action: 'select_frames' },
        name: 'Frames',
        selected: activeMode === 'frames',
        disabled: fps === undefined,
        action: () => {
          setMode('frames');
          setMenuAnchor(null);
        },
      },
    ],
    [activeMode, fps, setMode],
  );

  return (
    <FlexCenVer sx={{ gap: 0.25, flexShrink: 0, width: 'fit-content' }}>
      {showFrames ? (
        <FrameInput
          currentTime={currentTime}
          duration={duration}
          fps={fps}
          paused={paused}
          onTogglePlay={onTogglePlay}
          onSetTime={onSetTime}
        />
      ) : (
        <ThemedTimecodeInput
          value={currentTime}
          duration={duration}
          frameRate={fps ?? DEFAULT_TIME_INPUT_FPS}
          onChange={(time) => onSetTime(time, false)}
          onEditingStart={() => {
            if (!paused) onTogglePlay();
          }}
          style={{ width: TIME_INPUT_WIDTH, flexShrink: 0 }}
        />
      )}
      <FormatCaretButton
        open={Boolean(menuAnchor)}
        onClick={(event) => {
          setMenuAnchor(event.currentTarget instanceof HTMLElement ? event.currentTarget : null);
        }}
      />
      <AppContextMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        items={formatActions}
        width="120px"
        offset={{ top: '4px' }}
      />
    </FlexCenVer>
  );
}
